import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetBranches, useGetPaymentMethods } from "src/api/generic";
import { useGetOCAccountsList } from "src/api/operation-cost/accounts";
import { useGetSuppliersList } from "src/api/purchasing/suppliers";
import FilterIcon from "src/assets/svg-icons/aggregators/filter.svg";
import { SettingsPermissions } from "src/constants/permissions";
import { specificBranchHasPermission } from "src/utils";

interface IProps {
  open: boolean;
  handleClose: any;
  branchId: string;
  setBranchId: Function;
  refNum: string;
  setRefNum: Function;
  supplierId: string;
  setSupplierId: Function;
  status: string;
  setStatus: Function;
}

const FilterDrawer = (props: IProps) => {
  const {
    open,
    handleClose,
    branchId,
    setBranchId,
    refNum,
    setRefNum,
    supplierId,
    setSupplierId,
    status,
    setStatus,
  } = props;

  const [tempBranchId, setTempBranchId] = useState(branchId);
  const [tempRefNum, setTempRefNum] = useState(refNum);
  const [tempSupplierId, setTempSupplierId] = useState(supplierId);
  const [tempStatus, setTempStatus] = useState(status);

  // APIS
  const { data: branchesData } = useGetBranches();
  const { data: suppliersData } = useGetSuppliersList();
  const { t } = useTranslation("settings");
  const { t: generalT } = useTranslation("general");

  // permissions
  const branchesHavePermission =
    branchesData?.data.filter((branch) =>
      specificBranchHasPermission(
        SettingsPermissions.viewPaymentTransactions,
        branch.id,
      ),
    ) || [];

  const handleApply = () => {
    setBranchId(tempBranchId);
    setRefNum(tempRefNum);
    setSupplierId(tempSupplierId);
    setStatus(tempStatus);

    handleClose();
  };

  const handleClearFilters = () => {
    setTempBranchId("");
    setTempRefNum("");
    setTempSupplierId("");
    setTempStatus("all");

    setBranchId([]);
    setRefNum("");
    setSupplierId("");
    setStatus("all");

    handleClose();
  };

  const invoiceReceiptStatuses = [
    {
      value: "received",
      label: "received",
    },
    {
      value: "partially_received",
      label: "partiallyReceived",
    },
    {
      value: "not_received",
      label: "notReceived",
    },
    {
      value: "canceled",
      label: "cancelled",
    },
  ];

  return (
    <Drawer anchor={"right"} open={open} onClose={handleClose}>
      <Stack
        maxWidth={{ xs: "100%", sm: "376px" }}
        minWidth={{ xs: "100%", sm: "376px" }}
        borderRight={"1px solid #D0D5DD"}
        height={"100%"}
        maxHeight={"100vh"}
        top={"0"}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          p={"20px 27px"}
          bgcolor={"#fff"}
          boxShadow="0px 0px 40px 0px rgba(11, 24, 42, 0.07)"
        >
          <Button
            color="tertiary"
            variant="text"
            size="small"
            endIcon={<img src={FilterIcon} height={"16px"} alt="" />}
          >
            {generalT("filter")}
          </Button>

          <Button
            variant="outlined"
            color="tertiary"
            size="small"
            onClick={handleClose}
          >
            {generalT("hide")}
          </Button>
        </Box>
        <Stack spacing={"20px"} p={"40px 27px"}>
          <TextField
            label={generalT("referenceNumber")}
            variant="outlined"
            size="small"
            fullWidth
            value={tempRefNum}
            onChange={(e) => setTempRefNum(e.target.value)}
          />

          {/*  Branch Select */}
          <FormControl fullWidth size="small">
            <InputLabel id="demo-multiple-checkbox-label">
              {generalT("branch")}
            </InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              value={tempBranchId}
              onChange={(e) => setTempBranchId(e.target.value)}
              input={<OutlinedInput label={generalT("branch")} />}
            >
              {branchesHavePermission.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  <ListItemText primary={branch.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/*  Supplier Select */}
          <FormControl fullWidth size="small">
            <InputLabel id="demo-multiple-checkbox-label">
              {t("supplier")}
            </InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              value={tempSupplierId}
              onChange={(e) => setTempSupplierId(e.target.value)}
              input={<OutlinedInput label={generalT("supplier")} />}
            >
              {suppliersData?.data?.map((sup: any) => (
                <MenuItem key={sup.id} value={sup.id}>
                  <ListItemText primary={sup.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/*  type Select */}
          <FormControl fullWidth size="small">
            <InputLabel id="demo-multiple-checkbox-label">
              {generalT("status")}
            </InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              value={tempStatus}
              onChange={(e) => setTempStatus(e.target.value)}
              input={<OutlinedInput label={t("type")} />}
            >
              <MenuItem value={"all"}>
                <ListItemText primary={generalT("all")} />
              </MenuItem>
              {invoiceReceiptStatuses.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  <ListItemText primary={t(status.label)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* divider */}
          <Box width={"100%"} height={"1px"} bgcolor={"#CBD5E1"} />
        </Stack>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          p={"20px 27px"}
          bgcolor={"#fff"}
          boxShadow="0px 0px 40px 0px rgba(11, 24, 42, 0.07)"
          alignItems={"flex-end"}
          mt="auto"
        >
          <Button variant="contained" size="small" onClick={handleApply}>
            {generalT("apply")}
          </Button>
          <Button
            variant="outlined"
            color="tertiary"
            size="small"
            onClick={handleClearFilters}
          >
            {generalT("clear")}
          </Button>
        </Box>
      </Stack>
    </Drawer>
  );
};
export default FilterDrawer;
